/**
 * AuthModule — Unified authentication module.
 *
 * Sub-routes: login, signup, forgot-password, reset-password, verify-email
 * Renders full-screen (no topbar/bottomnav).
 */

import type { AweniContext } from '../../context/AweniContext';

type ViewUnmount = { unmount: () => void };

export default class AuthModule {
  private _container: HTMLElement | null = null;
  private _ctx: AweniContext | null = null;
  private _currentView: ViewUnmount | null = null;
  private _currentSubpath = '';

  async mount(container: HTMLElement, context: AweniContext): Promise<void> {
    this._container = container;
    this._ctx = context;

    // Parse initial subpath from hash
    const hash = window.location.hash.replace(/^#\/?/, '');
    const subpath = this._extractSubpath(hash);
    await this._navigateToView(subpath);
  }

  unmount(): void {
    this._currentView?.unmount();
    this._currentView = null;
    if (this._container) this._container.innerHTML = '';
  }

  /**
   * Called by ModuleViewport when hash changes within the auth module.
   */
  async onRouteChange(subpath: string): Promise<void> {
    await this._navigateToView(subpath || 'login');
  }

  private _extractSubpath(hash: string): string {
    // Hash formats: entry, guest, claim, login, signup, forgot-password, reset-password?token=xxx, verify-email?token=xxx
    const segments = hash.split('/').filter(Boolean);
    const ALLOWED = ['entry', 'guest', 'claim', 'login', 'signup', 'forgot-password', 'reset-password', 'verify-email'];

    if (segments.length >= 1) {
      const first = segments[0];
      if (ALLOWED.includes(first)) {
        return first;
      }
    }

    return 'entry';
  }

  private _getParams(): Record<string, string> {
    // Parse query params from hash: #/reset-password?token=xxx
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx === -1) return {};

    const params: Record<string, string> = {};
    const searchStr = hash.slice(qIdx + 1);
    for (const pair of searchStr.split('&')) {
      const [key, val] = pair.split('=');
      if (key) params[decodeURIComponent(key)] = decodeURIComponent(val || '');
    }
    return params;
  }

  private async _navigateToView(view: string): Promise<void> {
    if (!this._container || !this._ctx) return;
    if (view === this._currentSubpath && this._currentView) return;

    this._currentView?.unmount();
    this._currentView = null;
    this._currentSubpath = view;

    const params = this._getParams();
    const onNavigate = (targetView: string, extraParams?: Record<string, string>) => {
      const paramStr = extraParams
        ? '?' + Object.entries(extraParams).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
        : '';
      window.location.hash = `#/${targetView}${paramStr}`;
      this._navigateToView(targetView);
    };

    switch (view) {
      case 'entry': {
        const { mountEntryView } = await import('./views/EntryView');
        this._currentView = await mountEntryView(this._container, this._ctx, { onNavigate });
        break;
      }
      case 'guest': {
        const { mountGuestEntryView } = await import('./views/GuestEntryView');
        this._currentView = await mountGuestEntryView(this._container, this._ctx, { onNavigate });
        break;
      }
      case 'claim': {
        const { mountClaimView } = await import('./views/ClaimView');
        this._currentView = await mountClaimView(this._container, this._ctx, { onNavigate });
        break;
      }
      case 'login': {
        const { mountLoginView } = await import('./views/LoginView');
        this._currentView = await mountLoginView(this._container, this._ctx, { onNavigate });
        break;
      }
      case 'signup': {
        const { mountSignupView } = await import('./views/SignupView');
        this._currentView = await mountSignupView(this._container, this._ctx, { onNavigate });
        break;
      }
      case 'forgot-password': {
        const { mountForgotPasswordView } = await import('./views/ForgotPasswordView');
        this._currentView = await mountForgotPasswordView(this._container, this._ctx, { onNavigate });
        break;
      }
      case 'reset-password': {
        const { mountResetPasswordView } = await import('./views/ResetPasswordView');
        this._currentView = await mountResetPasswordView(this._container, this._ctx, {
          onNavigate,
          token: params.token,
        });
        break;
      }
      case 'verify-email': {
        const { mountVerifyEmailView } = await import('./views/VerifyEmailView');
        this._currentView = await mountVerifyEmailView(this._container, this._ctx, {
          onNavigate,
          token: params.token,
          email: params.email,
        });
        break;
      }
      default: {
        // Unknown auth view → default to the 3-button entry
        const { mountEntryView } = await import('./views/EntryView');
        this._currentView = await mountEntryView(this._container, this._ctx, { onNavigate });
      }
    }
  }
}
